import { useState } from "react";
import { Modal } from "antd";
import { CloseOutlined, KeyOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../hooks/useViewport";
import { modalStyles, modalWrapStyle } from "../../utils/modalStyles";
import ChangeCredentialsForm, { type CredentialKind } from "./ChangeCredentialsForm";

interface ChangeCredentialsModalProps {
  open: boolean;
  onClose: () => void;
  hasCode: boolean;
  hasPassword: boolean;
}

// „Смяна на код или парола" от профилното меню — СОБСТВЕНИТЕ код и парола на
// влезлия човек (на общ терминал — служителят, влязъл с кода си). Предлага се
// само това, което човекът вече има; първа парола/код се задава от Организация.
// Шелът е по cloudts-design/references/modals.md; Esc и клик на фона минават
// през antd `onCancel`, затова няма собствен Esc handler.
export default function ChangeCredentialsModal({
  open,
  onClose,
  hasCode,
  hasPassword,
}: ChangeCredentialsModalProps) {
  const { isMobile, isTablet } = useViewport();
  const width = isMobile ? undefined : isTablet ? 420 : 460;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      {/* Съдържанието е отделен компонент, за да тръгва на чисто (избран
          раздел, полета) при всяко отваряне — destroyOnHidden го демонтира. */}
      <ChangeCredentialsContent
        hasCode={hasCode}
        hasPassword={hasPassword}
        onClose={onClose}
      />
    </Modal>
  );
}

function ChangeCredentialsContent({
  hasCode,
  hasPassword,
  onClose,
}: {
  hasCode: boolean;
  hasPassword: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { isNarrow } = useViewport();
  // Кодът е първи — повечето хора (сервитьорите) имат само него.
  const [kind, setKind] = useState<CredentialKind>(hasCode ? "code" : "password");

  const title =
    hasCode && hasPassword
      ? t("credentials.modal.title")
      : hasCode
        ? t("credentials.modal.titleCode")
        : t("credentials.modal.titlePassword");
  const subtitle =
    kind === "code"
      ? t("credentials.modal.subtitleCode")
      : t("credentials.modal.subtitlePassword");

  return (
    <div className="bg-surface text-ink">
      <div
        className={`flex items-start gap-3 border-b border-shade-2 ${
          isNarrow ? "px-4 py-3.5" : "px-6 py-5"
        }`}
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-brand-tint">
          <KeyOutlined className="text-[20px] text-brand-deep-ink" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="m-0 text-[18px] font-bold leading-[1.2] tracking-[-0.3px] text-ink">
            {title}
          </h2>
          <p className="m-0 mt-1 text-[13px] font-medium leading-[1.4] text-body">
            {subtitle}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close")}
          className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-muted transition-colors duration-150 hover:bg-shade-1 hover:text-ink"
        >
          <CloseOutlined className="text-[14px]" />
        </button>
      </div>

      <ChangeCredentialsForm
        kind={kind}
        onKindChange={hasCode && hasPassword ? setKind : null}
        onDone={onClose}
      />
    </div>
  );
}
